import React, { useEffect, useContext, useState, useMemo } from "react"
import { get, isEmpty, remove, map } from "lodash"
import PropTypes from "prop-types"
import moment from "moment"
import { object, boolean } from "yup"

// Components
import { FileSelect } from "@4cplatform/elements/Forms"
import { H3, A, SmallText } from "@4cplatform/elements/Typography"
import { usePost, useDelete } from "@4cplatform/elements/Api"
import { Button } from "@4cplatform/elements/Molecules"
import { useTranslations } from "@4cplatform/elements/Translations"
import { addAlert } from "@4cplatform/elements/Alerts"
import {
  UploadedFilesWrapper,
  UploadedFile,
  UploadedFileMeta,
  ParagraphWrap
} from "./fileUploader.styles"

// Helpers
import { JourneyContext } from "../../journey.context"

const getErrorMessage = _fileType => {
  switch (_fileType) {
    case "COMPANY_THIRD_PARTY_PAYER":
    case "DEMAND_COMPANY_THIRD_PARTY_PAYER":
      return "Error notification! A valid third-party payer form must be uploaded to continue."
    case "PMC":
    default:
      return "MISSING_REQUIRED_FIELD"
  }
}

const FileUploader = ({
  fileType,
  fileSelectLabel,
  name,
  accept,
  validFileTypes,
  maxFileSize,
  isRequired,
  canAllowMultiple,
  apiEndpointPath
}) => {
  const t = useTranslations()
  const { data, formik, addToFormikValidationSchema, removeFromFormikValidationSchema } =
    useContext(JourneyContext)
  const [files, setFiles] = useState([])
  const [fileToUpload, setFileToUpload] = useState(null)
  const [fileToDelete, setFileToDelete] = useState(null)
  const [fileInputRef, setFileInputRef] = useState(null)

  const fileTypeToUpload = useMemo(() => {
    switch (fileType) {
      case "COMPANY_THIRD_PARTY_PAYER":
      case "DEMAND_COMPANY_THIRD_PARTY_PAYER":
        return "COMPANY_THIRD_PARTY_PAYER"
      case "PMC":
      default:
        return fileType
    }
  }, [fileType])

  // is invalid due to other fields
  const isInvalidDueToOthers = (() => {
    switch (fileType) {
      case "COMPANY_THIRD_PARTY_PAYER":
        return (
          get(formik, "values.application_form_received_from_client", false) ||
          get(formik, "values.form_filled_out_correctly", false)
        )
      case "DEMAND_COMPANY_THIRD_PARTY_PAYER":
        return true
      case "PMC":
      default:
        return get(formik, "values.pmc_received_and_meets_criteria", false)
    }
  })()
  const hasError = formik.errors[name] && formik.submitCount >= 1 && isInvalidDueToOthers
  const error = getErrorMessage(fileType)

  const isLocked = get(data, "journey.locked", false)

  const [upload, { loading: uploadLoading }] = usePost({
    endpoint: `/journeys/:journey/${apiEndpointPath || "files"}`,
    headers: {
      "Content-Type": "multipart/form-data"
    },
    params: {
      journey: get(data.journey, "slug", "")
    },
    onCompleted: res => {
      if (res.data?.length) {
        for (let i = 0, n = fileToUpload.length; i < n; i++) {
          if (res.data[i].id) fileToUpload[i].id = res.data[i].id
          if (res.data[i].url) fileToUpload[i].url = res.data[i].url
        }
        setFiles(current => [...res.data, ...current])
        fileInputRef.current.value = ""
        formik.setFieldValue(name, true)
        addAlert({
          message: t("FILE_UPLOAD_SUCCESS"),
          type: "success",
          dismissible: true,
          timeout: 5
        })
      }
    },
    onError: () => {
      addAlert({
        message: t("FILE_UPLOAD_ERROR"),
        type: "error",
        dismissible: true,
        timeout: 5
      })
    }
  })

  const [onDelete, { loading: deleteLoading }] = useDelete({
    endpoint: `/journeys/:journey/${apiEndpointPath || "files"}/:file`,
    onCompleted: () => {
      const filteredFiles =
        files.length === 1 ? [] : remove(files, file => file.id !== fileToDelete.id)
      setFiles(filteredFiles)
      if (isEmpty(filteredFiles)) formik.setFieldValue(name, false)

      setFileToDelete(null)
    },
    onError: () => {
      addAlert({
        message: t("FILE_DELETE_ERROR"),
        type: "error",
        dismissible: true,
        timeout: 5
      })
    }
  })

  const handleDrop = (inputValue, inputRef) => {
    if (uploadLoading) {
      return
    }
    setFileToUpload(inputValue)
    setFileInputRef(inputRef)
  }

  useEffect(() => {
    const uploadedFiles = get(data, `journey.${name}`)
    if (!isEmpty(uploadedFiles)) {
      setFiles(Array.isArray(uploadedFiles) ? uploadedFiles : [uploadedFiles])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isEmpty(files) && isInvalidDueToOthers) formik.setFieldValue(name, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, isInvalidDueToOthers])

  useEffect(() => {
    if (!isEmpty(formik.values)) {
      if (isInvalidDueToOthers) {
        addToFormikValidationSchema(
          object({
            [`${name}`]: boolean().oneOf([true], error).required()
          })
        )
      } else {
        removeFromFormikValidationSchema(name)
        formik.setFieldValue(name, false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values])

  useEffect(() => {
    if (fileToUpload && fileToUpload.length > 0) {
      const body = new FormData()
      let shouldUpload = false
      for (let i = 0, n = fileToUpload.length; i < n; i++) {
        if (!isEmpty(validFileTypes)) {
          const fileFormat = `.${fileToUpload[i].name.split(".").pop()}`

          const isInValidFormat = validFileTypes.split(",").includes(fileFormat)
          const isValidFileSize = fileToUpload[i].size * 0.000001 <= parseInt(maxFileSize)
          if (isInValidFormat && isValidFileSize) {
            body.append("files[]", fileToUpload[i])
            shouldUpload = true
          } else {
            let errorMessage = ""
            if (!isInValidFormat) errorMessage = `${t("FILE_UPLOAD_ERROR")}: Invalid file format.`
            if (!isValidFileSize)
              errorMessage = `${t(
                "FILE_UPLOAD_ERROR"
              )}: File size is too large. The file must be less than ${maxFileSize}MB`
            addAlert({
              message: errorMessage,
              type: "error",
              dismissible: true,
              timeout: 5
            })
            break
          }
        } else {
          body.append("files[]", fileToUpload[i])
          shouldUpload = true
        }
      }
      if (shouldUpload) {
        body.append("file_type", fileTypeToUpload)
        upload({ body })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileToUpload])

  useEffect(() => {
    if (!fileToDelete) return
    onDelete({
      params: {
        journey: get(data.journey, "slug", ""),
        file: fileToDelete.id
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileToDelete])

  useEffect(() => {
    // eslint-disable-next-line no-use-before-define
    fileNamesObject.files = files.map(file => file.file_name).join(", ") || "-"

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files])

  const renderUploadedFiles = () => {
    if (!isEmpty(files)) {
      return map(files, file => {
        if (!file) return
        const fileSize =
          file.size?.toString().length >= 7
            ? `${Math.round(file.size / 1000000)} MB`
            : `${Math.round(file.size / 1000)} KB`
        return (
          <UploadedFile key={file.id}>
            <UploadedFileMeta>
              <A href={file.url} rel="noopener noreferrer" target="_blank" download>
                <ParagraphWrap> {file.file_name}</ParagraphWrap>
              </A>
              <SmallText margin="0">
                {moment.utc(file.created_at).local().format("DD/MM/YYYY HH:mm:ss")}, {fileSize}
              </SmallText>
            </UploadedFileMeta>

            <Button
              onClick={() => setFileToDelete(file)}
              appearance="errorInline"
              trailingIcon="delete"
              isLoading={deleteLoading}
              isDisabled={isLocked}
              type="inline-button"
              name="delete_selected_hospital"
              margin="0 1rem 0"
            />
          </UploadedFile>
        )
      })
    }
    return []
  }

  return (
    <>
      <FileSelect
        errorPrefix="none"
        hasError={hasError}
        name={name}
        formik={formik}
        margin="0"
        isRequired={isRequired}
        label={fileSelectLabel}
        isLoading={uploadLoading || deleteLoading}
        isDisabled={isLocked}
        accept={accept}
        canAllowMultiple={canAllowMultiple}
        onDropCallback={handleDrop}
        onUploadCallback={(inputValue, inputRef) => {
          setFileToUpload(inputValue)
          setFileInputRef(inputRef)
        }}
      />
      {!isEmpty(files) && (
        <>
          <H3 margin="3rem 0 2rem 0">Uploaded File</H3>
          <UploadedFilesWrapper>{renderUploadedFiles()}</UploadedFilesWrapper>
        </>
      )}
    </>
  )
}

FileUploader.defaultProps = {
  fileType: "",
  fileSelectLabel: "",
  accept: "",
  validFileTypes: "",
  isRequired: true,
  canAllowMultiple: false,
  maxFileSize: 30
}

FileUploader.propTypes = {
  fileType: PropTypes.string,
  name: PropTypes.string,
  fileSelectLabel: PropTypes.string,
  accept: PropTypes.string,
  /**
   * Validate file formats
   */
  validFileTypes: PropTypes.string,
  /**
   * Whether or not multiple files can be uploaded
   */
  canAllowMultiple: PropTypes.bool,
  /**
   * Maximum file size to upload, default to 20 MB
   */

  maxFileSize: PropTypes.number,
  isRequired: PropTypes.bool,
  apiEndpointPath: PropTypes.string
}
export default FileUploader

// eslint-disable-next-line no-new-object
const fileNamesObject = new Object()

export { fileNamesObject }
